/**
 * Smart Paste Link Plugin for ProseMirror
 *
 * When user selects text and pastes a URL, automatically convert the selected text
 * into a link with that URL (instead of replacing the text). This is standard
 * behavior in Google Docs, Notion, Slack, Discord, and most modern editors.
 *
 * Features:
 * - Detects when pasted content is a single URL
 * - Converts selected text to link instead of replacing it
 * - Preserves existing formatting when possible
 * - Falls back to default paste if not a simple URL paste
 */

import { Plugin, PluginKey } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";
import { schema } from "../schema";

export const smartPasteLinkPluginKey = new PluginKey("smartPasteLink");

/**
 * Check if text is a valid URL.
 * Uses browser's URL constructor for robust validation.
 * Only allows http:// and https:// protocols.
 */
function isUrl(text: string): boolean {
  const trimmed = text.trim();

  // Must start with http:// or https://
  if (!/^https?:\/\//i.test(trimmed)) {
    return false;
  }

  // Use browser's URL parser for validation
  try {
    const url = new URL(trimmed);
    // Only allow http and https protocols
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    // Invalid URL
    return false;
  }
}

/**
 * Create the smart paste link plugin.
 */
export function createSmartPasteLinkPlugin(): Plugin {
  return new Plugin({
    key: smartPasteLinkPluginKey,

    props: {
      handlePaste(view: EditorView, event: ClipboardEvent): boolean {
        const { state } = view;
        const { selection } = state;

        // Only intercept if user has selected text (non-empty selection)
        if (selection.empty) {
          return false; // Let default paste handler work
        }

        // Get clipboard content
        const clipboardData = event.clipboardData;
        if (!clipboardData) {
          return false;
        }

        // Get plain text from clipboard
        const pastedText = clipboardData.getData("text/plain");
        if (!pastedText) {
          return false;
        }

        // Check if pasted content is a URL
        if (!isUrl(pastedText)) {
          return false; // Not a URL, let default paste work
        }

        // We have: selected text + pasted URL
        // Create a link mark and apply it to the selection
        const url = pastedText.trim();
        const linkMark = schema.marks.link.create({ href: url });

        // Get the range of selected text
        const { from, to } = selection;

        // Create transaction that:
        // 1. Removes existing link marks from selection (if any)
        // 2. Applies the new link mark
        const tr = state.tr
          .removeMark(from, to, schema.marks.link)
          .addMark(from, to, linkMark);

        view.dispatch(tr);

        // Prevent default paste behavior
        return true;
      },
    },
  });
}
